import { logError } from '../lib/debug-helpers';
import { APIError, ApiResponse, genericServiceError, TEN_MB_ROUNDED } from '../lib/_api-helpers';
import React from 'react';
import {
    CompletedPart,
    completeMultipartUpload,
    getMultipartPresignedUrls,
    getPresignedUrl,
    uploadFile,
    uploadPart,
} from '../lib/document-upload-helpers';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';

export type UseUploadRequestData = {
    upload: (file: File) => Promise<ApiResponse<UploadResults>>;
    uploading: boolean;
    errors: APIError[];
};

export type UploadResults = {
    s3Key: string;
    filename: string;
};

export default function useUploadRequest(): UseUploadRequestData {
    const navigate = useNavigate();

    const [errors, setErrors] = React.useState<APIError[]>([]);
    const [uploading, setUploading] = React.useState<boolean>(false);

    async function upload(file: File): Promise<ApiResponse<UploadResults>> {
        setUploading(true);
        setErrors([]);

        let token: string | undefined;

        try {
            const session = await fetchAuthSession();
            token = session.tokens?.accessToken?.toString();

            if (!token) {
                navigate('/signin');
                return genericServiceError<UploadResults>();
            }
        } catch (err) {
            navigate('/signin');
            return genericServiceError<UploadResults>();
        }

        try {
            if (file.size > TEN_MB_ROUNDED) {
                const presignedUrlsResponse = await getMultipartPresignedUrls(file, token);

                if (!presignedUrlsResponse.value || presignedUrlsResponse.errors?.length) {
                    throw new Error('Error getting multipart presigned URLs');
                }

                const numberOfParts = Math.ceil(file.size / TEN_MB_ROUNDED);
                const uploadsArray: Promise<ApiResponse<CompletedPart>>[] = [];

                for (let i = 0; i < numberOfParts; i++) {
                    const start = i * TEN_MB_ROUNDED;
                    const end = Math.min(start + TEN_MB_ROUNDED, file.size);
                    const blob = file.slice(start, end);
                    uploadsArray.push(uploadPart(blob, presignedUrlsResponse.value.presignedUrls[i], i + 1));
                }

                const uploadResponses = await Promise.all(uploadsArray);

                const failedUploads = uploadResponses.some((x) => !x.value || x.errors?.length);
                if (failedUploads) {
                    throw new Error('Some chunks failed to upload');
                }

                const completedParts = uploadResponses.map((x) => x.value).filter((x): x is CompletedPart => !!x);

                const completeUploadResponse = await completeMultipartUpload(
                    presignedUrlsResponse.value.uploadId,
                    presignedUrlsResponse.value.s3Key,
                    completedParts,
                    token,
                );

                if (!completeUploadResponse.value || completeUploadResponse.errors) {
                    throw new Error('Failed to complete multipart upload');
                }

                return {
                    value: {
                        s3Key: presignedUrlsResponse.value.s3Key,
                        filename: presignedUrlsResponse.value.filename,
                    },
                };
            } else {
                const response = await getPresignedUrl(file, token);

                if (!response.value || response.errors?.length) {
                    throw new Error('Error getting presigned URL for upload');
                }

                const uploadResponse = await uploadFile(
                    file,
                    response.value.presignedUrl,
                    response.value.presignedFormFields,
                );

                if (!uploadResponse.value) {
                    throw new Error('Upload failed');
                }

                return { value: { s3Key: response.value.s3Key, filename: response.value.filename } };
            }
        } catch (err: any) {
            logError(err);
            setErrors([{ field: 'global', code: 'Upload failed' }]);
            return genericServiceError<UploadResults>();
        } finally {
            setUploading(false);
        }
    }

    return { upload, uploading, errors };
}
